// Shared presentational cells for the Performance V2 tables and widgets
// (#4932 Phase 0.2 — extracted verbatim from performance-v2.tsx; no behavior
// change). Pure render components: parents own all state and data fetching.
import type React from 'react';
import { TrendingUp, TrendingDown, Columns3, Download } from 'lucide-react';

import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { downloadCsv, type ExportColumn } from '@/lib/csv-export';
import { cn } from '@/lib/utils';

// Shared categorical palette for the perf-v2 charts (donuts, bars, markers).
export const CHART_PURPLE = ['#290BD9', '#6D28D9', '#8B5CF6', '#A78BFA', '#C4B5FD', '#DDD6FE'];

// Strip the "Glitch | <type> | ..." prefix convention from campaign names for
// display. Geo campaigns render "<name> | <location>".
export function parseCampaignName(name: string): string {
  const parts = name.split(' | ');
  if (parts.length >= 3 && parts[0] === 'Glitch') {
    if (parts.length === 4) {
      if (parts[1] === 'Geo') return `${parts[3]} | ${parts[2]}`;
      return parts[3];
    }
    return parts[2];
  }
  return name;
}

export function fmt(value: number, s: string): string {
  if (value >= 1000) return `${s}${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  return `${s}${value.toFixed(2)}`;
}

export function MetricCard({
  label,
  value,
  subtitle,
  delta,
}: {
  label: string;
  value: string;
  subtitle?: string;
  /** Optional compare-period line (see DeltaLine); collapses when absent. */
  delta?: React.ReactNode;
}) {
  return (
    <Card className="p-4 border border-gray-200 shadow-sm bg-white">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="text-xl font-bold text-gray-900">{value}</p>
      {delta}
      {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
    </Card>
  );
}

// The standalone flavour of DeltaCell's delta region — same "new"/"flat"/±%
// language, for surfaces (MetricCard) that already render the current value
// themselves. Renders nothing when there is no previous period.
export function DeltaLine({
  current,
  previous,
  direction = 'higher-better',
}: {
  current: number;
  previous?: number | null;
  direction?: 'higher-better' | 'lower-better' | 'neutral';
}) {
  if (previous === undefined || previous === null) return null;
  if (previous === 0) {
    return current > 0 ? <p className="text-[10px] text-gray-500 mt-0.5">new</p> : null;
  }
  const deltaPct = ((current - previous) / previous) * 100;
  if (Math.abs(deltaPct) < 0.1) return <p className="text-[10px] text-gray-400 mt-0.5">flat</p>;
  const isImprovement =
    direction === 'higher-better' ? deltaPct > 0
    : direction === 'lower-better' ? deltaPct < 0
    : false;
  const colorClass =
    direction === 'neutral' ? 'text-gray-500'
    : isImprovement ? 'text-green-600'
    : 'text-red-600';
  const Arrow = deltaPct > 0 ? TrendingUp : TrendingDown;
  return (
    <p className={`flex items-center gap-0.5 text-[10px] mt-0.5 ${colorClass}`}>
      <Arrow className="h-2.5 w-2.5" />
      {Math.abs(deltaPct).toFixed(1)}% vs previous
    </p>
  );
}

export function EmptyChart({ icon: Icon, message }: { icon: React.ElementType; message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <Icon className="h-10 w-10 text-gray-300 mb-3" strokeWidth={1} />
      <p className="text-sm text-gray-400">{message}</p>
    </div>
  );
}

// Cell renderer that pairs a current value with its previous-period equivalent
// and renders an inline Δ% with ▲/▼ and semantic colour when compare-to is on.
// Direction encodes which way is "good" for the metric:
//   higher-better → CTR, conversions, conversion value, ROAS, impressions, clicks
//   lower-better  → CPA, CPC
//   neutral       → cost, raw spend (interpretation is contextual)
// When `previous` is undefined the delta region collapses, so the cell renders
// cleanly outside compare mode without conditional wrappers at the call site.
export function DeltaCell({
  current,
  previous,
  format,
  direction = 'higher-better',
}: {
  current: number;
  previous?: number;
  format: (value: number) => string;
  direction?: 'higher-better' | 'lower-better' | 'neutral';
}) {
  const hasPrev = previous !== undefined && previous !== null;
  const formatted = format(current);
  if (!hasPrev) return <span>{formatted}</span>;

  // "new" badge when the previous period had zero — percentage is undefined.
  if (previous === 0) {
    return (
      <div className="leading-tight">
        <div>{formatted}</div>
        {current > 0 && <div className="text-[10px] text-gray-500">new</div>}
      </div>
    );
  }

  const deltaPct = ((current - previous) / previous) * 100;
  const absDelta = Math.abs(deltaPct);
  // Sub-0.1% movement is rounding noise — render the value only.
  if (absDelta < 0.1) {
    return (
      <div className="leading-tight">
        <div>{formatted}</div>
        <div className="text-[10px] text-gray-400">flat</div>
      </div>
    );
  }

  const isImprovement =
    direction === 'higher-better' ? deltaPct > 0
    : direction === 'lower-better' ? deltaPct < 0
    : false;
  const colorClass =
    direction === 'neutral' ? 'text-gray-500'
    : isImprovement ? 'text-green-600'
    : 'text-red-600';
  const Arrow = deltaPct > 0 ? TrendingUp : TrendingDown;

  return (
    <div className="leading-tight">
      <div>{formatted}</div>
      <div className={cn('text-[10px] flex items-center gap-0.5', colorClass)}>
        <Arrow className="h-2.5 w-2.5" />
        {absDelta.toFixed(1)}%
      </div>
    </div>
  );
}

// #4300 — Campaign status pill. Colour follows the team's weekly report
// convention (LIMITED amber, ACTIVE/ELIGIBLE green, PAUSED grey, MISCONFIGURED
// red). Status reasons surface via a native tooltip — no extra component lift.
const CAMPAIGN_STATUS_STYLE: Record<string, { label: string; cls: string }> = {
  LIMITED:        { label: 'Limited',      cls: 'bg-amber-100 text-amber-800' },
  ELIGIBLE:       { label: 'Eligible',     cls: 'bg-green-100 text-green-800' },
  ENABLED:        { label: 'Active',       cls: 'bg-green-100 text-green-800' },
  PAUSED:         { label: 'Paused',       cls: 'bg-gray-100 text-gray-700' },
  REMOVED:        { label: 'Removed',      cls: 'bg-gray-100 text-gray-500' },
  ENDED:          { label: 'Ended',        cls: 'bg-gray-100 text-gray-500' },
  NOT_ELIGIBLE:   { label: 'Not eligible', cls: 'bg-red-100 text-red-700' },
  MISCONFIGURED:  { label: 'Issue',        cls: 'bg-red-100 text-red-700' },
  LEARNING:       { label: 'Learning',     cls: 'bg-blue-100 text-blue-700' },
  PENDING:        { label: 'Pending',      cls: 'bg-blue-100 text-blue-700' },
};

export function StatusPill({ status, reasons }: { status: string | null | undefined; reasons: string[] }) {
  if (!status) return <span className="text-xs text-gray-400">—</span>;
  const style = CAMPAIGN_STATUS_STYLE[status] ?? {
    label: status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase()),
    cls: 'bg-gray-100 text-gray-700',
  };
  const tooltip = reasons.length > 0
    ? reasons.map(r => r.replace(/_/g, ' ').toLowerCase()).join(', ')
    : undefined;
  return (
    <span
      className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase', style.cls)}
      title={tooltip}
    >
      {style.label}
    </span>
  );
}

// Glitch campaign STATE pill (Live / Draft / Paused / …). Distinct from the Google serving-status
// pill above: this is the campaign's lifecycle state in Glitch, which the team wanted visible on the
// performance tables (campaigns + ads).
const CAMPAIGN_STATE_STYLE: Record<string, { label: string; cls: string }> = {
  live:     { label: 'Live',     cls: 'bg-green-100 text-green-800' },
  paused:   { label: 'Paused',   cls: 'bg-amber-100 text-amber-800' },
  draft:    { label: 'Draft',    cls: 'bg-gray-100 text-gray-700' },
  creating: { label: 'Creating', cls: 'bg-blue-100 text-blue-700' },
  error:    { label: 'Error',    cls: 'bg-red-100 text-red-700' },
};

export function CampaignStatePill({ state }: { state: string | null | undefined }) {
  if (!state) return <span className="text-xs text-gray-400">—</span>;
  const style = CAMPAIGN_STATE_STYLE[state] ?? { label: state, cls: 'bg-gray-100 text-gray-700' };
  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase', style.cls)}>
      {style.label}
    </span>
  );
}

// Search Impression Share — percentage with a thin in-cell bar.
// Banding: <30 red, 30-70 amber, ≥70 green — same thresholds as OptScorePill.
export function ImpressionShareCell({ value }: { value: number | null }) {
  if (value == null) return <span className="text-xs text-gray-400">—</span>;
  const pct = value * 100;
  const fillCls = pct < 30 ? 'bg-red-400' : pct < 70 ? 'bg-amber-400' : 'bg-green-500';
  return (
    <div className="leading-tight w-20">
      <div className="text-sm">{pct.toFixed(1)}%</div>
      <div className="h-1 rounded bg-gray-100 overflow-hidden mt-0.5">
        <div className={cn('h-full', fillCls)} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
    </div>
  );
}

// Search Lost IS — both budget and rank in one cell so the cause is visible
// alongside the IS. Budget loss is the higher-signal one (a campaign LIMITED
// by budget can be unblocked by spend; rank loss is a competitive issue).
export function LostIsCell({ budget, rank }: { budget: number | null; rank: number | null }) {
  if (budget == null && rank == null) return <span className="text-xs text-gray-400">—</span>;
  const fmtPct = (v: number | null) => (v == null ? '—' : `${(v * 100).toFixed(0)}%`);
  return (
    <div className="leading-tight text-[11px]">
      <div className="text-gray-700">budget: <span className="font-medium">{fmtPct(budget)}</span></div>
      <div className="text-gray-700">rank: <span className="font-medium">{fmtPct(rank)}</span></div>
    </div>
  );
}

// Optimisation score — 0..1 from the API rendered as a 0-100 pill.
// Same threshold colours as ImpressionShareCell for consistency.
export function OptScorePill({ score }: { score: number | null }) {
  if (score == null) return <span className="text-xs text-gray-400">—</span>;
  const pct = score * 100;
  const cls =
    pct < 50 ? 'bg-red-100 text-red-700' :
    pct < 75 ? 'bg-amber-100 text-amber-800' :
    'bg-green-100 text-green-800';
  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold', cls)}>
      {pct.toFixed(0)}
    </span>
  );
}

const BID_STRATEGY_SHORT: Record<string, string> = {
  MAXIMIZE_CONVERSIONS:       'Max Conv.',
  MAXIMIZE_CONVERSION_VALUE:  'Max Conv. Value',
  MAXIMIZE_CLICKS:            'Max Clicks',
  TARGET_CPA:                 'Target CPA',
  TARGET_ROAS:                'Target ROAS',
  TARGET_IMPRESSION_SHARE:    'Target IS',
  TARGET_SPEND:               'Target Spend',
  MANUAL_CPC:                 'Manual CPC',
  MANUAL_CPM:                 'Manual CPM',
  ENHANCED_CPC:               'Enhanced CPC',
};

export function BidStrategyCell({ strategy }: { strategy: string | null }) {
  if (!strategy) return <span className="text-xs text-gray-400">—</span>;
  return <span className="text-xs text-gray-700">{BID_STRATEGY_SHORT[strategy] ?? strategy.replace(/_/g, ' ').toLowerCase()}</span>;
}

// Compact keyword status indicator — ENABLED green dot, PAUSED amber, REMOVED grey.
export function KeywordStatusBadge({ status }: { status: string | null }) {
  if (!status) return <span className="text-xs text-gray-400">—</span>;
  const dotCls =
    status === 'ENABLED' ? 'bg-green-500' :
    status === 'PAUSED' ? 'bg-amber-500' :
    status === 'REMOVED' ? 'bg-gray-400' :
    'bg-gray-300';
  const textCls =
    status === 'ENABLED' ? 'text-green-700' :
    status === 'PAUSED' ? 'text-amber-700' :
    'text-gray-500';
  const label = status.charAt(0) + status.slice(1).toLowerCase();
  return (
    <span className={cn('inline-flex items-center gap-1 text-[10px] font-medium', textCls)}>
      <span className={cn('w-1.5 h-1.5 rounded-full', dotCls)} />
      {label}
    </span>
  );
}

// Match-type chip. BROAD / PHRASE / EXACT — Google Ads' three match types.
// Lower-case rendering matches the team's report convention.
const MATCH_TYPE_LABEL: Record<string, string> = {
  EXACT: 'exact',
  PHRASE: 'phrase',
  BROAD: 'broad',
  BROAD_MATCH_MODIFIER: 'broad+',
};

export function MatchTypeBadge({ matchType }: { matchType: string | null }) {
  if (!matchType) return null;
  const label = MATCH_TYPE_LABEL[matchType] ?? matchType.toLowerCase();
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600">
      {label}
    </span>
  );
}

// #4296 — Search-term status pill. ADDED green = the term is now a managed
// keyword; EXCLUDED grey w/ strikethrough = it's negative-listed; NONE =
// neither, the actionable case.
export function SearchTermStatusBadge({ status }: { status: string | null }) {
  if (!status || status === 'NONE' || status === 'UNKNOWN') {
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-50 text-gray-500">—</span>;
  }
  const cls =
    status === 'ADDED' ? 'bg-green-100 text-green-800' :
    status === 'EXCLUDED' ? 'bg-gray-200 text-gray-600 line-through' :
    'bg-gray-100 text-gray-600';
  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium uppercase', cls)}>
      {status.toLowerCase().replace(/_/g, ' ')}
    </span>
  );
}

// Consolidated metric cell — four signals (Imp · Clicks · Conv · Cost) in a
// single column. Used by the Ads, Keywords, and Search Terms tables to
// reduce horizontal density and match the consolidated style on
// /optimizations. Value on top, lowercase metric label underneath in a
// muted gray so the row reads at a glance.
export function PerformanceCell({
  impressions, clicks, conversions, cost, currency,
}: {
  impressions: number;
  clicks: number;
  conversions: number;
  cost: number;
  currency: string;
}) {
  return (
    <div className="flex items-center gap-3 text-sm text-gray-900 whitespace-nowrap">
      <span>{impressions.toLocaleString()} <span className="text-[10px] text-gray-500">imp</span></span>
      <span>{clicks.toLocaleString()} <span className="text-[10px] text-gray-500">clicks</span></span>
      <span>{conversions.toFixed(0)} <span className="text-[10px] text-gray-500">conv</span></span>
      <span>{fmt(cost, currency)} <span className="text-[10px] text-gray-500">cost</span></span>
    </div>
  );
}

// #4310 — Reusable column-picker popover. Extracted because three tables
// (Campaigns / Ads / Ad-Group) all need the same affordance. The picker is
// stateless — toggle + reset are passed in, and the parent owns the
// 'visible columns' Set and the persistence write-back.
export function ColumnsPopover({
  columnsMeta,
  visibleIds,
  totalIds,
  onToggle,
  onReset,
}: {
  columnsMeta: { id: string; label: string; pinned?: boolean }[];
  visibleIds: Set<string>;
  totalIds: Set<string>;
  onToggle: (id: string) => void;
  onReset: () => void;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="h-9 text-gray-900 gap-1.5">
          <Columns3 className="h-4 w-4" />
          <span>Columns</span>
          {visibleIds.size < totalIds.size && (
            <span className="text-[10px] text-gray-500">({visibleIds.size}/{totalIds.size})</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-2">
        <div className="px-1 py-1 mb-1 flex items-center justify-between">
          <span className="text-[10px] font-semibold tracking-wider text-gray-500 uppercase">Show columns</span>
          <button
            type="button"
            onClick={onReset}
            className="text-[10px] text-[#290BD9] hover:text-[#2309C5]"
          >
            Reset
          </button>
        </div>
        <div className="space-y-0.5">
          {columnsMeta.map(col => {
            const checked = visibleIds.has(col.id);
            const disabled = !!col.pinned;
            return (
              <label
                key={col.id}
                className={cn(
                  'flex items-center gap-2 px-2 py-1.5 rounded text-sm cursor-pointer hover:bg-gray-50',
                  disabled && 'cursor-not-allowed opacity-60 hover:bg-transparent',
                )}
              >
                <Checkbox
                  checked={checked}
                  disabled={disabled}
                  onCheckedChange={() => onToggle(col.id)}
                />
                <span className="text-gray-900">{col.label}</span>
                {disabled && <span className="ml-auto text-[10px] text-gray-400">always on</span>}
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// #4302 — Per-table CSV export button. Tiny outline button with a Download
// icon. Clicking pulls the user-facing data (post-filter / post-sort) into
// a CSV that mirrors the Google Ads UI export shape (title / date-range /
// column headers / rows). Disabled when there's no data to export.
export function ExportCsvButton<T>({
  rows,
  columns,
  title,
  dateRange,
  startDate,
  endDate,
  filenameSlug,
  disabled,
}: {
  rows: T[];
  columns: ExportColumn<T>[];
  title: string;
  dateRange: string;
  startDate: string;
  endDate: string;
  filenameSlug?: string;
  disabled?: boolean;
}) {
  const handleClick = () => downloadCsv(rows, columns, { title, dateRange, filenameSlug }, startDate, endDate);
  return (
    <Button
      variant="outline"
      size="sm"
      className="h-7 text-xs gap-1.5 text-gray-700"
      onClick={handleClick}
      disabled={disabled || rows.length === 0}
    >
      <Download className="h-3 w-3" />
      Export CSV
    </Button>
  );
}

export function EmptyTabState({ icon: Icon, title, description }: { icon: React.ElementType; title: string; description: string }) {
  return (
    <Card className="border border-gray-200 shadow-sm bg-white">
      <div className="flex flex-col items-center justify-center py-16 text-center px-6">
        <Icon className="h-12 w-12 text-gray-300 mb-4" strokeWidth={1} />
        <h3 className="text-sm font-semibold text-gray-900 mb-1">{title}</h3>
        <p className="text-sm text-gray-400 max-w-md">{description}</p>
      </div>
    </Card>
  );
}
